import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Login } from '@pages/Login';
import { Register } from '@pages/Register';
import { ForgotPassword } from '@pages/ForgotPassword';
import { ResetPassword } from '@pages/ResetPassword';
import { Dashboard } from '@pages/Dashboard';
import { Listings } from '@pages/Listings';
import { Opportunities } from '@pages/Opportunities';
import { FirstSelection } from '@pages/FirstSelection';
import { Rejected } from '@pages/Rejected';
import { MyProperties } from '@pages/MyProperties';
import { Portfolio } from '@pages/Portfolio';
import { Comparateur } from '@pages/Comparateur';
import { InvestorProfilePage } from '@pages/InvestorProfilePage';
import { Profile } from '@pages/Profile';
import { Analysis } from '@pages/Analysis';
import { Admin } from '@pages/Admin';
import { FaqPage } from '@pages/FaqPage';
import { useAuth } from '@hooks/useAuth';
import { ThemeProvider } from '@components/ThemeProvider';
import { ToastContainer } from '@components/feedback/ToastContainer';
import { Spinner } from '@components/common/Spinner';
import '@styles/globals.css';

// Root redirect component - redirects based on auth state
const RootRedirect = () => {
  const { isAuthenticated } = useAuth();
  return <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />;
};

interface ProtectedRouteProps {
  children: React.ReactNode;
  isAuthenticated: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, isAuthenticated }) => {
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

export const App: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="text-gray-600 dark:text-gray-400 mt-4">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/" element={<RootRedirect />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/listings"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Listings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rejected"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Rejected />
              </ProtectedRoute>
            }
          />
          <Route
            path="/first-selection"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <FirstSelection />
              </ProtectedRoute>
            }
          />
          <Route
            path="/opportunities"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Opportunities />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-properties"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <MyProperties />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portfolio"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Portfolio />
              </ProtectedRoute>
            }
          />
          <Route
            path="/comparateur"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Comparateur />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={<Navigate to="/profile?tab=settings" replace />}
          />
          <Route
            path="/profil-investisseur"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <InvestorProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analysis/:id"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Analysis />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Admin />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
      <ToastContainer />
    </ThemeProvider>
  );
};

export default App;
